import * as React from "react";

import { CloseIcon } from "@/assets/icons";
import { cn } from "@/lib/utils";

import * as DialogPrimitive from "@radix-ui/react-dialog";

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const dialogOverlayBaseClassName = "fixed inset-0 z-[999] bg-black/80";
const dialogOverlayAnimationClassName =
  "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0";
const dialogContentBaseClassName =
  "fixed outline-0 left-[50%] top-[50%] z-[999] grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border border-neutral-200 bg-white p-6 shadow-lg sm:rounded-lg";
const dialogContentAnimationClassName =
  "duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]";

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay> & {
    disableAnimation?: boolean;
  }
>(({ className, disableAnimation = false, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      dialogOverlayBaseClassName,
      !disableAnimation && dialogOverlayAnimationClassName,
      className
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    isHideClose?: boolean;
    zIndex?: number;
    manualOverlay?: boolean;
    // Opt-in override for the backdrop classes. Pass e.g. "bg-transparent" to
    // keep modal semantics (focus trap, outside-click) while letting an
    // underlying popup show through. Defaults to the opaque backdrop.
    overlayClassName?: string;
    disableAnimation?: boolean;
  }
>(
  (
    {
      className,
      children,
      isHideClose,
      zIndex,
      manualOverlay = false,
      overlayClassName,
      disableAnimation = false,
      ...props
    },
    ref
  ) => (
    <DialogPortal>
      {manualOverlay ? (
        <div
          className={cn(
            "fixed inset-0 bg-black/80",
            !disableAnimation && "animate-in fade-in-0",
            overlayClassName
          )}
          style={{ zIndex }}
        />
      ) : (
        <DialogOverlay
          className={overlayClassName}
          disableAnimation={disableAnimation}
          style={{ zIndex }}
        />
      )}
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          dialogContentBaseClassName,
          !disableAnimation && dialogContentAnimationClassName,
          className
        )}
        style={{ zIndex }}
        {...props}
      >
        {children}
        {!isHideClose && (
          <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-white transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-neutral-950 focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-neutral-100 data-[state=open]:text-neutral-500 dark:focus:ring-neutral-300">
            <CloseIcon className="w-4 h-4 fill-txtBody" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  )
);
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-1.5 text-center sm:text-left",
      className
    )}
    {...props}
  />
);
DialogHeader.displayName = "DialogHeader";

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className
    )}
    {...props}
  />
);
DialogFooter.displayName = "DialogFooter";

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-lg font-semibold leading-none tracking-tight",
      className
    )}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-neutral-500 dark:text-neutral-400", className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger
};
